import {
  ProgramConfig,
  ProgramStat,
  ProgramStatsResponse,
  UserOwningOrganization,
} from '@cibo/core'
import { useOwningOrg } from '@cibo/profile'
import { useProgramContent, useProgramModel, useProgramStats } from '@cibo/programs'
import { Box, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material'
import numeral from 'numeral'
import { useTranslation } from 'react-i18next'
import { programOrgLimits } from './utils'

interface ProgramTargetsProps {
  programId: string
}

export const ProgramTargets = ({ programId }: ProgramTargetsProps) => {
  const { t } = useTranslation('impact/ProgramStats')
  const stats = useProgramStats({})

  const program = useProgramModel({ programId })
  const programStats = stats.data?.programs.find(s => s.programId === programId)
  const { data: owningOrg } = useOwningOrg()
  const orgLimits = programOrgLimits(
    program.data?.program.ledger.capsPerOrg,
    owningOrg,
    programStats?.perOrgLimit
  )
  const hideOrgLimits =
    !orgLimits ||
    !orgLimits.length ||
    (orgLimits.length === 1 && orgLimits[0].orgId === owningOrg?.id)

  const hideTierLimits = !program.data?.hasTiers

  if (hideOrgLimits && hideTierLimits) return null

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Stack>
          <Typography variant="h6">{t('programTargets')}</Typography>
          <Typography variant="subtitle1">{t('acres')}</Typography>
        </Stack>
        <Stack spacing={1.5}>
          {!hideOrgLimits && (
            <OrgLimits
              owningOrg={owningOrg}
              program={program.data?.program}
              programStats={programStats}
            />
          )}
          {!hideTierLimits && <TierLimits program={program.data?.program} stats={stats.data} />}
        </Stack>
      </Stack>
    </Paper>
  )
}

const LimitLineItem = ({
  committedLabel,
  isFull,
  limitLabel,
  value,
  name,
}: {
  committedLabel: string
  isFull: boolean
  limitLabel: string
  value: number
  name?: string
}) => {
  const { t } = useTranslation('impact/ProgramStats')
  return (
    <Stack direction="row" justifyContent="space-between" key={name}>
      <Typography width="50%">{name}</Typography>
      <Stack alignItems="center" direction="row" width="50%">
        {isFull ? (
          <Chip color="success" label={t('full')} variant="outlined" size="small" />
        ) : (
          <>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
                {committedLabel}
              </Typography>
            </Box>
            <Box sx={{ width: '100%', mr: 1, ml: 1 }}>
              <LinearProgress variant="determinate" value={value} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">
                {limitLabel}
              </Typography>
            </Box>
          </>
        )}
      </Stack>
    </Stack>
  )
}

const OrgLimits = ({
  owningOrg,
  program,
  programStats,
}: {
  owningOrg?: UserOwningOrganization
  program?: ProgramConfig
  programStats?: ProgramStat
}) => {
  const { t } = useTranslation('impact/ProgramStats')
  const orgLimits = programOrgLimits(
    program?.ledger.capsPerOrg,
    owningOrg,
    programStats?.perOrgLimit
  )

  return (
    <>
      <Typography fontWeight="bold">{t('retailPartners')}</Typography>
      {orgLimits?.map(
        orgLimit =>
          !!orgLimit.maxValue && (
            <LimitLineItem
              name={orgLimit.name}
              committedLabel={numeral(orgLimit.committedAgainstLimit).format('0a')}
              isFull={orgLimit.committedAgainstLimit >= orgLimit.maxValue}
              limitLabel={numeral(orgLimit.maxValue).format('0a')}
              value={(orgLimit.committedAgainstLimit / orgLimit.maxValue) * 100}
            />
          )
      )}
    </>
  )
}
const TierLimits = ({
  stats,
  program,
}: {
  stats?: ProgramStatsResponse
  program?: ProgramConfig
}) => {
  const { t } = useTranslation('impact/ProgramStats')
  const content = useProgramContent(program?.programId)
  const tierStats = stats?.programs.filter(
    (a: ProgramStat) => a.parentProgramId === program?.programId && a.isTier
  )
  return (
    <>
      <Typography fontWeight="bold">{t('retailPartners')}</Typography>
      {program?.tiers?.map(tier => {
        const tierStat = tierStats?.find(a => a.programId === tier.id)
        const committedAcres = tierStat?.committed.acres || 0
        return (
          tier.maxAcres && (
            <LimitLineItem
              name={content?.data?.content.fields.tiers?.[tier.id]}
              committedLabel={numeral(tierStat?.committed.acres).format('0a')}
              isFull={committedAcres >= tier.maxAcres}
              limitLabel={numeral(tier.maxAcres).format('0a')}
              value={(committedAcres / tier.maxAcres) * 100}
            />
          )
        )
      })}
    </>
  )
}
